import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

const MODE_COLORS = {
  REFLECT: 'text-violet-400 border-violet-500/30 bg-violet-500/5',
  CONSOLIDATE: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/5',
  CURIOSITY: 'text-amber-400 border-amber-500/30 bg-amber-500/5',
  DISCOVER: 'text-sky-400 border-sky-500/30 bg-sky-500/5',
  UNKNOWN: 'text-muted-foreground border-border bg-muted/5',
};

const MODE_GLOW = {
  REFLECT: 'shadow-violet-500/10',
  CONSOLIDATE: 'shadow-emerald-500/10',
  CURIOSITY: 'shadow-amber-500/10',
  DISCOVER: 'shadow-sky-500/10',
  UNKNOWN: '',
};

export default function HeartbeatPulse({ heartbeat }) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!heartbeat?.created_at) return;
    const update = () => {
      const diff = Math.floor((Date.now() - new Date(heartbeat.created_at).getTime()) / 1000);
      setElapsed(diff);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [heartbeat?.created_at]);

  const mode = heartbeat?.mode || 'UNKNOWN';
  const modeColor = MODE_COLORS[mode] || MODE_COLORS.UNKNOWN;
  const glow = MODE_GLOW[mode] || '';

  // 5-minute cycle = 300 seconds
  const cycleProgress = Math.min((elapsed % 300) / 300, 1);
  const minutesAgo = Math.floor(elapsed / 60);

  if (!heartbeat) {
    return (
      <Card className="border-dashed border-muted-foreground/20">
        <CardContent className="p-6 text-center text-muted-foreground">
          <div className="text-sm">No heartbeat recorded yet.</div>
          <div className="text-xs mt-1">The heartbeat fires every 5 minutes once content is ingested.</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn('relative overflow-hidden shadow-lg transition-all', glow)}>
      {/* Breathing indicator */}
      <div className={cn(
        'absolute top-4 right-4 h-3 w-3 rounded-full animate-pulse',
        mode === 'REFLECT' && 'bg-violet-400',
        mode === 'CONSOLIDATE' && 'bg-emerald-400',
        mode === 'CURIOSITY' && 'bg-amber-400',
        mode === 'DISCOVER' && 'bg-sky-400',
        mode === 'UNKNOWN' && 'bg-muted-foreground',
      )} />

      <CardContent className="p-6 space-y-4">
        {/* Mode + timing */}
        <div className="flex items-center gap-3">
          <Badge variant="outline" className={cn('text-xs font-mono', modeColor)}>
            {mode}
          </Badge>
          <span className="text-xs text-muted-foreground">
            cycle {heartbeat.cycle_number} &middot; {minutesAgo < 1 ? 'just now' : `${minutesAgo}m ago`}
          </span>
        </div>

        {/* Thought preview */}
        <p className="text-sm leading-relaxed text-foreground/80 italic">
          &ldquo;{heartbeat.thought_preview}&rdquo;
        </p>

        {/* Stats row */}
        <div className="flex gap-4 text-xs text-muted-foreground">
          <span>{heartbeat.events_processed} events</span>
          <span>{heartbeat.insights_created} insights</span>
          <span>{heartbeat.patterns_found} patterns</span>
          <span>
            {heartbeat.connections_confirmed} confirmed /
            {heartbeat.connections_rejected} rejected
          </span>
        </div>

        {/* Cycle progress bar */}
        <div className="h-0.5 w-full bg-muted rounded-full overflow-hidden">
          <div
            className={cn(
              'h-full rounded-full transition-all duration-1000',
              mode === 'REFLECT' && 'bg-violet-500/50',
              mode === 'CONSOLIDATE' && 'bg-emerald-500/50',
              mode === 'CURIOSITY' && 'bg-amber-500/50',
              mode === 'DISCOVER' && 'bg-sky-500/50',
              mode === 'UNKNOWN' && 'bg-muted-foreground/50',
            )}
            style={{ width: `${cycleProgress * 100}%` }}
          />
        </div>
      </CardContent>
    </Card>
  );
}
